import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-dark-400 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl md:text-6xl font-futuristic font-bold mb-6">
            <span className="gradient-text">About CHILL GAMING</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We are dedicated to providing high-quality gaming accessories for Switch, PC, and other consoles. All items are tested before shipping to ensure functionality. Our goal is to deliver reliable products, fast shipping, and good service for every gamer.
          </p>
        </motion.div>

        {/* Mission Section */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants}>
              <h2 className="text-3xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                CHILL GAMING was established with a simple yet powerful vision: to provide gamers with the highest quality accessories for their Switch, PC, and console gaming needs. We believe that great gaming experiences start with reliable, well-tested equipment.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Every product in our catalog undergoes rigorous testing before shipping to ensure functionality and reliability. From controllers to charging docks, we're committed to delivering products that enhance your gaming performance and enjoyment.
              </p>
            </motion.div>
            
            <motion.div 
              className="relative"
              variants={itemVariants}
            >
              <div className="bg-gradient-to-br from-primary-500/20 to-accent-500/20 rounded-2xl p-8 backdrop-blur-sm border border-white/10">
                <div className="text-center">
                  <div className="text-6xl mb-4">🎮</div>
                  <h3 className="text-2xl font-bold text-white mb-4">Quality First</h3>
                  <p className="text-gray-300">
                    Our team carefully selects and tests every gaming accessory to ensure it meets our high standards for functionality and durability.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Values Section */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-3xl font-bold text-center text-white mb-12"
            variants={itemVariants}
          >
            Core Values
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: '🎯',
                title: 'Reliability',
                description: 'Every product is carefully tested before shipping to ensure functionality and durability for your gaming needs.'
              },
              {
                icon: '🚀',
                title: 'Fast Shipping',
                description: 'We prioritize quick and secure delivery to get your gaming accessories to you as soon as possible.'
              },
              {
                icon: '🛡️',
                title: 'Quality Service',
                description: 'Our dedicated support team is committed to providing excellent customer service for every gamer.'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                className="card-glass p-8 rounded-lg text-center"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-4">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Section */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="bg-gradient-to-r from-primary-500/10 via-accent-500/10 to-secondary-500/10 rounded-2xl p-8 border border-white/10">
            <motion.h2 
              className="text-3xl font-bold text-center text-white mb-8"
              variants={itemVariants}
            >
              Why Choose CHILL GAMING
            </motion.h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <motion.div variants={itemVariants}>
                <h3 className="text-xl font-semibold text-white mb-4">🎮 Gaming Focus</h3>
                <p className="text-gray-300 mb-6">
                  We specialize in gaming accessories for Switch, PC, and console gamers. Our products are designed with gamers' needs in mind, ensuring compatibility and performance.
                </p>
                
                <h3 className="text-xl font-semibold text-white mb-4">⚡ Tested Quality</h3>
                <p className="text-gray-300">
                  All items undergo thorough testing before shipping to ensure they work perfectly with your gaming setup and provide reliable performance during gameplay.
                </p>
              </motion.div>
              
              <motion.div variants={itemVariants}>
                <h3 className="text-xl font-semibold text-white mb-4">🔧 Functionality First</h3>
                <p className="text-gray-300 mb-6">
                  Our priority is delivering products that work as advertised. We test every controller, charger, and accessory to ensure they meet our functionality standards.
                </p>
                
                <h3 className="text-xl font-semibold text-white mb-4">🌐 Reliable Service</h3>
                <p className="text-gray-300">
                  From fast shipping to quality customer support, we're committed to providing a smooth experience for every gamer who shops with us.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Team Section */}
        <motion.section
          className="mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-3xl font-bold text-center text-white mb-12"
            variants={itemVariants}
          >
            Our Commitment
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: '🎮',
                title: 'Quality Products',
                description: 'We carefully select and test every gaming accessory to ensure it meets our high standards for functionality and durability.'
              },
              {
                icon: '🚀',
                title: 'Fast Shipping',
                description: 'Quick and secure delivery to get your gaming accessories to you as soon as possible, wherever you are.'
              },
              {
                icon: '🛡️',
                title: 'Customer Service',
                description: 'Our dedicated support team is here to help with any questions or concerns about your gaming accessories.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="card-glass p-6 rounded-lg text-center"
                variants={itemVariants}
                whileHover={{ y: -5 }}
              >
                <div className="text-5xl mb-4">{item.icon}</div>
                <h3 className="text-lg font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-gray-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Stats Section */}
        <motion.section
          className="text-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-3xl font-bold text-white mb-12"
            variants={itemVariants}
          >
            Our Promise
          </motion.h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '100%', label: 'Tested Products' },
              { number: 'Fast', label: 'Shipping' },
              { number: '24/7', label: 'Support' },
              { number: 'Quality', label: 'Guaranteed' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-6"
              >
                <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 text-sm uppercase tracking-wide">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default About;
